'use client';

import React from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetSystemVolumeQuery } from 'src/api/redux';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import {
  applyDisplayMode,
  applyCaptureRate,
} from 'src/components/Profiles/shared/widgets/utils';
import MonthlyAvgBadge from 'src/components/Profiles/shared/widgets/MonthlyAvgBadge';
import CaptureRateBadge from 'src/components/Profiles/shared/widgets/CaptureRateBadge';
import {
  getBenchmarkState,
  HERO_COLOR,
} from 'src/components/Profiles/shared/widgets/shared/benchmark';
import TargetBadge from 'src/components/Profiles/shared/widgets/shared/TargetBadge';
import { useResolveSystemId } from './useResolveSystemId';

const SystemVolume: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;

  const codeSetIds = [
    ...(widget?.code_sets ?? []),
    ...(widget?.code_set ? [widget.code_set] : []),
  ].map((cs: { id: number }) => cs.id);

  const encounterIds = (widget?.encounters ?? []).map(
    (enc: { id: number }) => enc.id
  );

  const systemId = useResolveSystemId(
    entityDetails,
    widget?.network_rollup === true
  );

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;

  const hasSources = codeSetIds.length > 0 || encounterIds.length > 0;

  const { data, isLoading: queryLoading } = useGetSystemVolumeQuery(
    {
      systemId: systemId || '',
      codeSetIds: codeSetIds.length > 0 ? codeSetIds : undefined,
      encounterIds: encounterIds.length > 0 ? encounterIds : undefined,
      minYear: minYear ?? undefined,
      maxYear: maxYear ?? undefined,
      filterSetId: widget?.filter_set?.id ?? undefined,
    },
    { skip: !systemId || !hasSources }
  );

  const hasData = !!data;
  const displayMode = widget?.display_mode;
  const captureRate = widget?.capture_rate;
  const isMonthlyAvg = displayMode === 'monthly_average';
  const isLoading = queryLoading || !!widgetConfig?.isLoading;

  const codeSetRows = (data?.code_sets ?? []).map((cs) => ({
    key: cs.code_set?.id ?? 0,
    label: cs.code_set?.label ?? 'Unknown',
    claims: Number(cs.claims),
    patients: Number(cs.patients),
  }));

  const encounterRows = (data?.encounters ?? []).map((enc) => ({
    key: `enc-${enc.encounter?.id ?? 0}`,
    label: enc.encounter?.label ?? 'Unknown',
    claims: Number(enc.claims),
    patients: Number(enc.patients),
  }));

  const volumeRows = [...codeSetRows, ...encounterRows];

  const volumeType = widget?.volume_type;
  const showClaims = !volumeType || volumeType === 'claims';
  const showPatients = !volumeType || volumeType === 'patients';
  const isSingleRow = volumeRows.length === 1 && hasData;

  const benchmark = widget?.benchmark ?? null;
  const benchmarkOperator = widget?.benchmark_operator ?? 'greater_than';

  const VALUE_CELL = 'py-2 text-right font-semibold text-teal-900 tabular-nums';

  const formatValue = (value: number) =>
    handleNumericValue(
      applyCaptureRate(applyDisplayMode(value, displayMode), captureRate)
    );

  const renderHero = () => {
    const row = volumeRows[0];
    const showPatientsAsHero = volumeType === 'patients';
    const heroRawValue = showPatientsAsHero ? row.patients : row.claims;
    const heroValue = formatValue(heroRawValue);
    const heroLabel = showPatientsAsHero ? 'Patients' : 'Claims';

    const hasSubtitle = !volumeType;
    const subtitleRawValue = showPatientsAsHero ? row.claims : row.patients;
    const subtitleValue = formatValue(subtitleRawValue);
    const subtitleLabel = showPatientsAsHero ? 'Claims' : 'Patients';

    const heroNumeric = applyCaptureRate(
      applyDisplayMode(heroRawValue, displayMode),
      captureRate
    );
    const benchmarkState =
      benchmark !== null
        ? getBenchmarkState(heroNumeric, benchmark, benchmarkOperator)
        : null;
    const heroColor = benchmarkState
      ? HERO_COLOR[benchmarkState]
      : 'text-sky-600';

    return (
      <div
        className="flex flex-col gap-1"
        data-testid="system-volume-hero"
      >
        <div className="flex items-baseline gap-3">
          <span
            className={`text-4xl font-bold tabular-nums ${heroColor}`}
            data-testid="system-volume-hero-value"
            data-benchmark-state={benchmarkState ?? 'none'}
            aria-label={`${heroLabel}: ${heroValue}${
              benchmarkState ? `, ${benchmarkState} benchmark` : ''
            }`}
          >
            {heroValue}
          </span>
          <span
            className="text-sm font-semibold text-neutral-500"
            data-testid="system-volume-hero-label"
          >
            {heroLabel}
          </span>
        </div>
        {hasSubtitle && (
          <p
            className="text-sm text-neutral-500"
            data-testid="system-volume-hero-subtitle"
          >
            {subtitleValue} {subtitleLabel}
          </p>
        )}
      </div>
    );
  };

  const renderTable = () => (
    <div data-testid="system-volume-stats">
      <table className="w-full text-sm" aria-label="System volume stats">
        <thead>
          <tr className="border-b border-neutral-100">
            <th className="text-left py-2" />
            {showClaims && (
              <th className="text-right py-2 font-medium text-neutral-500 text-xs w-24">
                Claims
              </th>
            )}
            {showPatients && (
              <th className="text-right py-2 font-medium text-neutral-500 text-xs w-24">
                Patients
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {volumeRows.map((row, index) => (
            <tr
              key={row.key}
              className={
                index < volumeRows.length - 1
                  ? 'border-b border-neutral-100'
                  : ''
              }
              data-testid={`system-volume-code-set-${row.key}`}
            >
              <th scope="row" className="py-2 font-medium text-primary text-left">{row.label}</th>
              {showClaims && (
                <td
                  className={VALUE_CELL}
                  data-testid={`system-volume-claims-${row.key}`}
                >
                  {formatValue(row.claims)}
                </td>
              )}
              {showPatients && (
                <td
                  className={VALUE_CELL}
                  data-testid={`system-volume-patients-${row.key}`}
                >
                  {formatValue(row.patients)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // No system found at the required level — show empty state
  const noSystem = !systemId && !isLoading;

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="system-volume"
      role="region"
      aria-label={widget?.label ?? 'System Volume'}
    >
      <WidgetHeader
        fallbackTitle="System Volume"
        trailing={
          <div
            className="flex items-center gap-1.5"
            data-testid="system-volume-trailing"
          >
            {isMonthlyAvg && <MonthlyAvgBadge />}
            {captureRate != null && <CaptureRateBadge rate={captureRate} />}
            {isSingleRow && benchmark !== null && (
              <TargetBadge
                target={benchmark}
                testId="system-volume-target-badge"
              />
            )}
          </div>
        }
      />
      <div className={isSingleRow ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        {isLoading ? (
          <WidgetLoading data-testid="system-volume-loading" />
        ) : noSystem || !hasData || volumeRows.length === 0 ? (
          <WidgetEmpty data-testid="system-volume-empty" variant="full" message="No volume data available" />
        ) : isSingleRow ? (
          renderHero()
        ) : (
          renderTable()
        )}
      </div>
    </Card>
  );
};

export default SystemVolume;
